/**
 * Imagem final de um passo para exportar: o recorte do zoom (se ligado) com o marcador do
 * clique desenhado, em JPEG. No browser (canvas); a extensão e a web dão a imagem original.
 */
import { PRODUCT, stepView, type Box, type Step } from '@nolens/domain';
import { drawAnnotations } from './annotate-canvas';

export async function renderStepBlob(
  step: Step,
  source: Blob,
  opts: { accent?: string; maxW?: number } = {},
): Promise<Blob> {
  const maxW = opts.maxW ?? 1400;
  const accent = opts.accent ?? PRODUCT.accent;
  const bmp = await createImageBitmap(source);
  const { crop, marker, showMarker } = stepView(step);
  const sx = crop.x * bmp.width;
  const sy = crop.y * bmp.height;
  const sw = crop.w * bmp.width;
  const sh = crop.h * bmp.height;
  const scale = Math.min(1, maxW / sw);
  const W = Math.round(sw * scale);
  const H = Math.round(sh * scale);
  const cv = new OffscreenCanvas(W, H);
  const ctx = cv.getContext('2d')!;
  ctx.drawImage(bmp, sx, sy, sw, sh, 0, 0, W, H);
  bmp.close();
  drawAnnotations(ctx, step.annotations, crop, W, H);
  if (showMarker) {
    const r = Math.max(16, Math.min(W, H) * 0.035);
    ctx.beginPath();
    ctx.arc(marker.x * W, marker.y * H, r, 0, Math.PI * 2);
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = accent;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = Math.max(3, r * 0.16);
    ctx.strokeStyle = accent;
    ctx.stroke();
  }
  return cv.convertToBlob({ type: 'image/jpeg', quality: 0.86 });
}

/** Formato das capturas guardadas: WebP é ~2× mais leve do que JPEG com a mesma nitidez. */
export const CAPTURE_TYPE = 'image/webp';

/**
 * Desfoca áreas de uma captura de forma irreversível (pixeliza em blocos grandes e esbate):
 * o texto deixa de ser legível e não pode ser recuperado da imagem exportada.
 */
export async function pixelateImage(blob: Blob, areas: Box[], quality = 0.9): Promise<Blob> {
  if (!areas.length) return blob;
  const bmp = await createImageBitmap(blob);
  const cv = new OffscreenCanvas(bmp.width, bmp.height);
  const ctx = cv.getContext('2d')!;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const W = cv.width;
  const H = cv.height;
  const block = Math.max(8, Math.round(Math.min(W, H) / 90));
  for (const a of areas) {
    const x = Math.max(0, Math.floor(W * a.x));
    const y = Math.max(0, Math.floor(H * a.y));
    const w = Math.min(W - x, Math.ceil(W * a.w));
    const h = Math.min(H - y, Math.ceil(H * a.h));
    if (w < 1 || h < 1) continue;
    const sw = Math.max(1, Math.round(w / block));
    const sh = Math.max(1, Math.round(h / block));
    const small = new OffscreenCanvas(sw, sh);
    const sctx = small.getContext('2d')!;
    sctx.imageSmoothingEnabled = true;
    sctx.drawImage(cv, x, y, w, h, 0, 0, sw, sh);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(small, 0, 0, sw, sh, x, y, w, h);
    ctx.restore();
  }
  return cv.convertToBlob({ type: CAPTURE_TYPE, quality });
}

export async function blobToDataUrl(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000)
    bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return 'data:' + (blob.type || 'image/jpeg') + ';base64,' + btoa(bin);
}

/** Descarrega um ficheiro gerado no browser. */
export function download(data: Blob, name: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(data);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/** Nome de ficheiro a partir do título (sem título, `fallback`). */
export function slug(s: string, fallback = 'guia'): string {
  return (
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || fallback
  );
}
